/**
 * Questionnaire Data Mapper
 *
 * Transforms questionnaire responses into the flat schema expected by the backend.
 * This ensures all session configuration fields are properly extracted and passed
 * to prevent cache collisions.
 */

export interface QuestionnaireData {
  // Session configuration
  sessionLength?: number | string;
  environment?: string;
  meditationStyle?: string;
  seekingGoal?: string;
  desiredFeeling?: string;
  voiceStyle?: string;

  // Voice preferences
  voiceGender?: string;
  specific_voice_id?: string;  // Standardized field name
  backgroundMusicFile?: string;
  includeBackgroundSounds?: boolean;
  backgroundMusicVolumeDb?: number;

  // Personal details
  age?: number | string;
  occupation?: string;
  stressors?: string;
  personalGoals?: string;
  personalChallenges?: string;
  clientBiography?: string;

  // Client information
  clientFirstName?: string;
  clientLastName?: string;
  practitionerName?: string;

  // Biography toggle
  usePersonalBiography?: boolean;

  // Category-specific responses
  [key: string]: any;
}

export interface FlatSessionConfig {
  // Category & length
  session_length: string;

  // Session configuration
  environment: string;
  meditation_style: string;
  seeking_goal: string;
  desired_feeling: string;
  voice_style: string;

  // Voice & audio
  voice_gender: string;
  specific_voice_id?: string;  // Standardized field name
  background_music_file?: string;
  include_background_sounds: boolean;
  background_music_volume_db: number;

  // Personalization
  client_biography?: string;
  client_first_name?: string;
  client_last_name?: string;
  practitioner_name?: string;

  // Raw questionnaire for cache key
  questionnaire_responses: Record<string, any>;
}

/**
 * Default values for session configuration
 */
const DEFAULTS = {
  environment: 'calm',
  meditation_style: 'guided',
  voice_style: 'calm',
  seeking_goal: 'personalized session',
  desired_feeling: 'calm',
  voice_gender: 'male',
  include_background_sounds: true,
  background_music_volume_db: -20,
  session_length: '10 minutes',
};

/**
 * Normalize session length to standard format
 */
function normalizeSessionLength(length: number | string | undefined): string {
  if (!length) return DEFAULTS.session_length;

  // If it's already a string with "minutes", return as-is
  if (typeof length === 'string' && length.includes('minute')) {
    return length;
  }

  // If it's a number or string number, add "minutes"
  const numericLength = typeof length === 'number' ? length : parseInt(length, 10);
  if (isNaN(numericLength)) return DEFAULTS.session_length;

  return `${numericLength} minutes`;
}

/**
 * Build client biography from questionnaire fields
 * Respects the usePersonalBiography toggle - returns undefined if toggle is false
 */
function buildClientBiography(data: QuestionnaireData): string | undefined {
  // If biography toggle is explicitly set to false, don't include biography
  if (data.usePersonalBiography === false) {
    return undefined;
  }

  const parts: string[] = [];

  if (data.age) {
    parts.push(`Age: ${data.age}`);
  }

  if (data.occupation) {
    parts.push(`Occupation: ${data.occupation}`);
  }

  if (data.stressors) {
    parts.push(`Current stressors: ${data.stressors}`);
  }

  // Handle both personalGoals and goals (different field names)
  const goals = data.personalGoals || data.goals;
  if (goals) {
    parts.push(`Goals: ${goals}`);
  }

  if (data.personalChallenges) {
    parts.push(`Challenges: ${data.personalChallenges}`);
  }

  if (data.hobbies) {
    parts.push(`Hobbies: ${data.hobbies}`);
  }

  if (data.relationship) {
    parts.push(`Relationship status: ${data.relationship}`);
  }

  // If explicit biography provided, use that (prioritize in order)
  if (data.clientBiography) {
    return data.clientBiography;
  }

  if (data.biography) {
    return data.biography;
  }

  // Otherwise, build from parts
  return parts.length > 0 ? parts.join('. ') : undefined;
}

/**
 * Map questionnaire data to flat backend schema
 */
export function mapQuestionnaireToBackendSchema(
  questionnaireData: QuestionnaireData | null | undefined,
  options: {
    voiceGender?: string;
    specific_voice_id?: string;  // Standardized field name
    backgroundMusicFile?: string;
  } = {}
): FlatSessionConfig {
  const data = questionnaireData || {};

  // Extract and normalize all fields
  const config: FlatSessionConfig = {
    // Session length
    session_length: normalizeSessionLength(data.sessionLength),

    // Session configuration (extract from questionnaire, with fallbacks to existing field names)
    environment: data.environment || DEFAULTS.environment,
    meditation_style: data.meditationStyle || data.preferredStyle || DEFAULTS.meditation_style,
    seeking_goal: data.seekingGoal || data.primaryGoal || DEFAULTS.seeking_goal,
    desired_feeling: data.desiredFeeling || DEFAULTS.desired_feeling,
    voice_style: data.voiceStyle || data.voiceType || DEFAULTS.voice_style,

    // Voice & audio (merge questionnaire with options)
    voice_gender: data.voiceGender || options.voiceGender || DEFAULTS.voice_gender,
    specific_voice_id: data.specific_voice_id || options.specific_voice_id,  // Standardized field name
    background_music_file: data.backgroundMusicFile || data.backgroundMusicSelection || options.backgroundMusicFile,
    include_background_sounds: data.includeBackgroundSounds ?? data.backgroundSounds ?? DEFAULTS.include_background_sounds,
    background_music_volume_db: data.backgroundMusicVolumeDb || DEFAULTS.background_music_volume_db,

    // Personalization
    client_biography: buildClientBiography(data),
    // FIX: Don't extract client/practitioner names from questionnaire data
    // These should ONLY come from the component's explicit props based on session type
    // The hook will handle them appropriately (only for individual client sessions)
    client_first_name: undefined,
    client_last_name: undefined,
    practitioner_name: undefined,

    // Raw questionnaire (for cache key generation)
    questionnaire_responses: { ...data },
  };

  return config;
}

/**
 * Extract category-specific fields from questionnaire
 * (For backward compatibility with old code that expects category_specific_data)
 */
export function extractCategorySpecificData(
  questionnaireData: QuestionnaireData | null | undefined
): Record<string, any> {
  const config = mapQuestionnaireToBackendSchema(questionnaireData);

  return {
    environment: config.environment,
    meditation_style: config.meditation_style,
    seeking_goal: config.seeking_goal,
    desired_feeling: config.desired_feeling,
    voice_style: config.voice_style,
    include_background_sounds: config.include_background_sounds,
    client_biography: config.client_biography,
  };
}

/**
 * Validate that required fields are present
 */
export function validateSessionConfig(config: FlatSessionConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!config.session_length) {
    errors.push('Session length is required');
  }

  if (!config.voice_gender) {
    errors.push('Voice gender is required');
  }

  if (!config.environment) {
    errors.push('Environment is required');
  }

  if (!config.meditation_style) {
    errors.push('Meditation style is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
